import { FormikValues } from "formik";
import {
  DocumentType,
  Option,
  SecuritiesFormikValues,
  Step,
  StepEndpointSecurities,
  StepOneSecurities as StepOneSecuritiesType,
} from "../../types";
import styles from "../../styles/securities.module.scss";
import { validationsForLanguage } from "../../validations";
import {
  isEmptyFile,
  maxFileSize,
  minFileSize,
  parseDateToSend,
  preconfirmationPhone,
  serializableFileAsFile,
} from "../../utils";
import {
  addSendedStep,
  HolderData,
  onUpdateCompletedStep,
  onUpdateFormData,
  onUpdateStep,
} from "../../reducers/securitiesReducer";
import { validProofOfAddressTypes } from "../../utils";
import {
  labels,
  placeholders,
  texts,
  title,
  tooltips,
} from "../../i18n/securities/stepOne";
import { fileInfoText } from "../../i18n/inputs";
import {
  civilStatusOptions,
  dependantsOptions,
  educationLevelOptions,
  genderOptions,
} from "../../i18n/securities/options";
import InfoIcon from "../../assets/images/icons/ui/tooltip.svg";
import { RootState } from "../../app/store";
import { holder } from "../../i18n/securities/preconfirmationStep";
import {
  CreateDraftProps,
  sendIndividualDocument,
  sendStep,
} from "../../connectors/connectors";

// Personal information form

const StepOne = ({
  language,
  countriesOptions,
  currentHolder,
  holdersData,
  saveFileToFirestore,
}: {
  language: "es" | "en";
  countriesOptions: Option[];
  currentHolder: number;
  holdersData: HolderData[];
  saveFileToFirestore?: (fileName: string, type: DocumentType) => void;
}): Step => {
  const validations = validationsForLanguage(language);

  const { formSectionTitle } = styles;

  return {
    step: 1,
    name: `${title[language]} ${
      currentHolder !== 1 ? `- (${holder[language]} ${currentHolder})` : ""
    }`,
    titleOfStep: title[language],
    label: title[language],
    fields: [
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "section1",
        text: (
          <>
            <p className={formSectionTitle}>
              {texts[language].prefix} 1: {texts[language].section1}
            </p>
          </>
        ),
      },
      {
        name: "name",
        label: labels[language].name,
        type: "text",
        initialValue: "",
        placeholder: placeholders[language].name,
        validation: validations.smallStringRequired,
      },
      {
        name: "lastName",
        label: labels[language].lastName,
        type: "text",
        initialValue: "",
        placeholder: placeholders[language].lastName,
        validation: validations.smallStringRequired,
      },
      {
        name: "motherName",
        label: labels[language].motherName,
        type: "text",
        initialValue: "",
        placeholder: placeholders[language].motherName,
        validation: validations.smallStringRequired,
      },
      {
        name: "gender",
        label: labels[language].gender,
        type: "select",
        initialValue: "",
        placeholder: placeholders[language].gender,
        validation: validations.radioOrSingleSelectRequired(
          genderOptions[language]
        ),
        options: genderOptions[language],
      },
      {
        name: "countryOfBirth",
        type: "select",
        label: labels[language].countryOfBirth,
        placeholder: placeholders[language].countryOfBirth,
        validation: validations.radioOrSingleSelectRequired(countriesOptions),
        options: countriesOptions,
        selectType: "single",
        initialValue: "",
      },
      {
        name: "birthDate",
        label: labels[language].birthDate,
        type: "date",
        initialValue: "",
        placeholder: placeholders[language].birthDate,
        validation: validations.previousDateRequired,
        columns: 2,
      },
      {
        name: "civilStatus",
        type: "select",
        label: labels[language].civilStatus,
        placeholder: placeholders[language].civilStatus,
        validation: validations.radioOrSingleSelectRequired(
          civilStatusOptions[language]
        ),
        options: civilStatusOptions[language],
        selectType: "single",
        initialValue: "",
        columns: 2,
      },
      {
        name: "educationLevel",
        type: "select",
        label: labels[language].educationLevel,
        placeholder: placeholders[language].educationLevel,
        validation: validations.radioOrSingleSelectRequired(
          educationLevelOptions[language]
        ),
        options: educationLevelOptions[language],
        selectType: "single",
        initialValue: "",
      },
      {
        name: "dependants",
        label: labels[language].dependants,
        type: "select",
        placeholder: placeholders[language].dependants,
        validation: validations.radioOrSingleSelectRequired(
          dependantsOptions[language]
        ),
        initialValue: "",
        options: dependantsOptions[language],
        tooltip: {
          label: "",
          content: tooltips[language].dependantsTooltip,
          icon: InfoIcon,
        },
      },
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "section2",
        text: (
          <p className={formSectionTitle}>
            {texts[language].prefix} 2: {texts[language].section2}
          </p>
        ),
      },
      {
        name: "mobilePhoneAR",
        type: "phone",
        country: "ar",
        label: labels[language].mobilePhone,
        validation: validations.internationalPhoneRequired,
        initialValue: "",
        conditionalBool: (_1, _2, state?: RootState) =>
          !state?.securities.USCitizen,
        formatInPreConfirmation: preconfirmationPhone,
      },
      {
        name: "mobilePhoneUS",
        type: "phone",
        country: "us",
        label: labels[language].mobilePhone,
        validation: validations.internationalPhoneRequired,
        initialValue: "",
        conditionalBool: (_1, _2, state?: RootState) =>
          !!state?.securities.USCitizen,
        formatInPreConfirmation: preconfirmationPhone,
      },
      {
        name: "homePhoneAR",
        type: "phone",
        country: "ar",
        label: labels[language].homePhone,
        validation: validations.internationalPhone,
        initialValue: "",
        showOptionalLabel: true,
        conditionalBool: (_1, _2, state?: RootState) =>
          !state?.securities.USCitizen,
        formatInPreConfirmation: preconfirmationPhone,
      },
      {
        name: "homePhoneUS",
        type: "phone",
        country: "us",
        label: labels[language].homePhone,
        validation: validations.internationalPhone,
        initialValue: "",
        showOptionalLabel: true,
        conditionalBool: (_1, _2, state?: RootState) =>
          !!state?.securities.USCitizen,
        formatInPreConfirmation: preconfirmationPhone,
      },
      {
        name: "workPhoneAR",
        type: "phone",
        country: "ar",
        label: labels[language].workPhone,
        validation: validations.internationalPhone,
        initialValue: "",
        showOptionalLabel: true,
        conditionalBool: (_1, _2, state?: RootState) =>
          !state?.securities.USCitizen,
        formatInPreConfirmation: preconfirmationPhone,
      },
      {
        name: "workPhoneUS",
        type: "phone",
        country: "us",
        label: labels[language].workPhone,
        validation: validations.internationalPhone,
        initialValue: "",
        showOptionalLabel: true,
        conditionalBool: (_1, _2, state?: RootState) =>
          !!state?.securities.USCitizen,
        formatInPreConfirmation: preconfirmationPhone,
      },
      {
        name: "email",
        type: "text",
        label: labels[language].email,
        placeholder: placeholders[language].email,
        validation: validations.stringRequired,
        initialValue: "",
        conditionalBool: () => currentHolder !== 1,
      },
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "section3",
        text: (
          <>
            <p className={formSectionTitle}>
              {texts[language].prefix} 3:{" "}
              {currentHolder > 1
                ? texts[language].section3SubsecuentHolders
                : texts[language].section3}
            </p>
          </>
        ),
      },
      {
        name: "addressesSameAsFirstHolders",
        type: "checkbox",
        label: labels[language].addressesSameAsFirstHolders,
        initialValue: false,
        validation: validations.checkbox,
        conditionalBool: () => currentHolder !== 1,
      },
      {
        name: "taxAddresses.country",
        type: "select",
        label: labels[language].taxAddresses.country,
        placeholder: placeholders[language].taxAddresses.country,
        validation: validations.radioOrSingleSelectRequired(countriesOptions),
        options: countriesOptions,
        selectType: "single",
        initialValue: "",
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders } = values;
          return currentHolder !== 1 ? !addressesSameAsFirstHolders : true;
        },
        formatInPreConfirmation: (value, formValues) => {
          const { addressesSameAsFirstHolders } = formValues || {};
          const firstHolder = holdersData.find(
            (holder) => holder.currentHolder === 1
          );
          const { taxAddresses } = firstHolder?.formValues || {};

          return addressesSameAsFirstHolders ? taxAddresses?.country : value;
        },
      },
      {
        name: "taxAddresses.fullAddress",
        type: "address",
        label: labels[language].taxAddresses.fullAddress,
        validation: validations.mediumStringRequired,
        initialValue: "",
        placeholder: placeholders[language].taxAddresses.fullAddress,
        country: "Argentina",
        onChange: (values, setFieldValue) => {
          const zipCode = values.formattedAddress?.postalCode;
          const state = values.formattedAddress?.state;
          const city = values.formattedAddress?.city;
          const street = values.formattedAddress?.street;
          const number = values.formattedAddress?.streetNumber;

          setFieldValue("taxAddresses.zipCode", zipCode);
          setFieldValue("taxAddresses.state", state);
          setFieldValue("taxAddresses.city", city);
          setFieldValue(
            "taxAddresses.fullAddress",
            `${street} ${number}`,
            true
          );
        },
        autoComplete: "off",
        formatInPreConfirmation: (_, formValues) => {
          const { addressesSameAsFirstHolders, taxAddresses } =
            formValues || {};

          const firstHolder = holdersData.find(
            (holder) => holder.currentHolder === 1
          );

          const { taxAddresses: taxAddressesFirstHolder } =
            firstHolder?.formValues || {};

          const { zipCode, floor, apartment, fullAddress } =
            addressesSameAsFirstHolders
              ? taxAddressesFirstHolder
              : taxAddresses;

          return `${fullAddress}${floor ? `, ${floor}` : ""} ${
            apartment ? `-${apartment}` : ""
          }, ${zipCode}`;
        },
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders } = values;
          return currentHolder !== 1 ? !addressesSameAsFirstHolders : true;
        },
      },
      {
        name: "taxAddresses.zipCode",
        type: "text",
        label: labels[language].taxAddresses.zipCode,
        validation: validations.zipCodeRequired,
        initialValue: "",
        placeholder: placeholders[language].taxAddresses.zipCode,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders } = values;
          return currentHolder !== 1 ? !addressesSameAsFirstHolders : true;
        },
      },
      {
        name: "taxAddresses.floor",
        type: "text",
        label: labels[language].taxAddresses.floor,
        validation: validations.smallString,
        initialValue: "",
        placeholder: placeholders[language].taxAddresses.floor,
        columns: 2,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders } = values;
          return currentHolder !== 1 ? !addressesSameAsFirstHolders : true;
        },
      },
      {
        name: "taxAddresses.apartment",
        type: "text",
        label: labels[language].taxAddresses.apartment,
        validation: validations.smallString,
        initialValue: "",
        placeholder: placeholders[language].taxAddresses.apartment,
        columns: 2,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders } = values;
          return currentHolder !== 1 ? !addressesSameAsFirstHolders : true;
        },
      },
      {
        name: "taxAddresses.city",
        type: "text",
        label: labels[language].taxAddresses.city,
        validation: validations.smallStringRequired,
        initialValue: "",
        columns: 2,
        placeholder: placeholders[language].taxAddresses.city,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders } = values;
          return currentHolder !== 1 ? !addressesSameAsFirstHolders : true;
        },
        formatInPreConfirmation: (value, formValues) => {
          const { addressesSameAsFirstHolders } = formValues || {};

          const firstHolder = holdersData.find(
            (holder) => holder.currentHolder === 1
          );

          const { taxAddresses } = firstHolder?.formValues || {};

          const cityFirstHolder = taxAddresses?.city;

          return addressesSameAsFirstHolders ? cityFirstHolder : value;
        },
      },
      {
        name: "taxAddresses.state",
        type: "text",
        label: labels[language].taxAddresses.state,
        validation: validations.smallStringRequired,
        initialValue: "",
        columns: 2,
        placeholder: placeholders[language].taxAddresses.state,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders } = values;
          return currentHolder !== 1 ? !addressesSameAsFirstHolders : true;
        },
        formatInPreConfirmation: (value, formValues) => {
          const { addressesSameAsFirstHolders } = formValues || {};

          const firstHolder = holdersData.find(
            (holder) => holder.currentHolder === 1
          );

          const { taxAddresses } = firstHolder?.formValues || {};

          const stateFirstHolder = taxAddresses?.state;

          return addressesSameAsFirstHolders ? stateFirstHolder : value;
        },
      },
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "section4",
        text: (
          <>
            <p className={formSectionTitle}>
              {texts[language].prefix} 4: {texts[language].section4}
            </p>
          </>
        ),
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders } = values;
          return currentHolder !== 1 ? !addressesSameAsFirstHolders : true;
        },
      },
      {
        name: "postalAddressDifferent",
        label: labels[language].postalAddressDifferent,
        initialValue: false,
        validation: validations.checkbox,
        type: "checkbox",
        allConditionalsTrue: true,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders } = values;
          return currentHolder !== 1 ? !addressesSameAsFirstHolders : true;
        },
      },
      {
        name: "postalAddresses.country",
        type: "select",
        label: labels[language].postalAddresses.country,
        placeholder: placeholders[language].postalAddresses.country,
        validation: validations.radioOrSingleSelectRequired(countriesOptions),
        options: countriesOptions,
        selectType: "single",
        initialValue: "",
        allConditionalsTrue: true,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders, postalAddressDifferent } =
            values;
          return currentHolder !== 1
            ? !addressesSameAsFirstHolders && !!postalAddressDifferent
            : !!postalAddressDifferent;
        },
        formatInPreConfirmation: (value, formValues) => {
          const { addressesSameAsFirstHolders } = formValues || {};

          const firstHolder = holdersData.find(
            (holder) => holder.currentHolder === 1
          );

          const { postalAddresses } = firstHolder?.formValues || {};

          const countryFirstHolder = postalAddresses?.country;

          return addressesSameAsFirstHolders ? countryFirstHolder : value;
        },
      },
      {
        name: "postalAddresses.fullAddress",
        type: "address",
        label: labels[language].postalAddresses.fullAddress,
        validation: validations.mediumStringRequired,
        initialValue: "",
        placeholder: placeholders[language].postalAddresses.fullAddress,
        allConditionalsTrue: true,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders, postalAddressDifferent } =
            values;
          return currentHolder !== 1
            ? !addressesSameAsFirstHolders && !!postalAddressDifferent
            : !!postalAddressDifferent;
        },
        onChange: (values, setFieldValue) => {
          const zipCode = values.formattedAddress?.postalCode;
          const state = values.formattedAddress?.state;
          const city = values.formattedAddress?.city;
          const street = values.formattedAddress?.street;
          const number = values.formattedAddress?.streetNumber;

          setFieldValue("postalAddresses.zipCode", zipCode);
          setFieldValue("postalAddresses.state", state);
          setFieldValue("postalAddresses.city", city);
          setFieldValue(
            "postalAddresses.fullAddress",
            `${street} ${number}`,
            true
          );
        },
        autoComplete: "off",
        formatInPreConfirmation: (_, formValues) => {
          const { addressesSameAsFirstHolders, postalAddresses } =
            formValues || {};

          const firstHolder = holdersData.find(
            (holder) => holder.currentHolder === 1
          );

          const { postalAddresses: postalAddressesFirstHolder } =
            firstHolder?.formValues || {};

          const { zipCode, floor, apartment, fullAddress } =
            addressesSameAsFirstHolders
              ? postalAddressesFirstHolder
              : postalAddresses;

          return `${fullAddress}${floor ? `, ${floor}` : ""} ${
            apartment ? `- ${apartment}` : ""
          }, ${zipCode}`;
        },
      },
      {
        name: "postalAddresses.zipCode",
        type: "text",
        label: labels[language].postalAddresses.zipCode,
        validation: validations.zipCodeRequired,
        initialValue: "",
        placeholder: placeholders[language].postalAddresses.zipCode,
        allConditionalsTrue: true,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders, postalAddressDifferent } =
            values;
          return currentHolder !== 1
            ? !addressesSameAsFirstHolders && !!postalAddressDifferent
            : !!postalAddressDifferent;
        },
      },
      {
        name: "postalAddresses.floor",
        type: "text",
        label: labels[language].postalAddresses.floor,
        validation: validations.smallString,
        initialValue: "",
        placeholder: placeholders[language].postalAddresses.floor,
        columns: 2,
        allConditionalsTrue: true,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders, postalAddressDifferent } =
            values;
          return currentHolder !== 1
            ? !addressesSameAsFirstHolders && !!postalAddressDifferent
            : !!postalAddressDifferent;
        },
      },
      {
        name: "postalAddresses.apartment",
        type: "text",
        label: labels[language].postalAddresses.apartment,
        validation: validations.smallString,
        initialValue: "",
        placeholder: placeholders[language].postalAddresses.apartment,
        columns: 2,
        allConditionalsTrue: true,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders, postalAddressDifferent } =
            values;
          return currentHolder !== 1
            ? !addressesSameAsFirstHolders && !!postalAddressDifferent
            : !!postalAddressDifferent;
        },
      },
      {
        name: "postalAddresses.city",
        type: "text",
        label: labels[language].postalAddresses.city,
        validation: validations.smallStringRequired,
        initialValue: "",
        columns: 2,
        placeholder: placeholders[language].postalAddresses.city,
        allConditionalsTrue: true,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders, postalAddressDifferent } =
            values;
          return currentHolder !== 1
            ? !addressesSameAsFirstHolders && !!postalAddressDifferent
            : !!postalAddressDifferent;
        },
        formatInPreConfirmation: (value, formValues) => {
          const { addressesSameAsFirstHolders } = formValues || {};

          const firstHolder = holdersData.find(
            (holder) => holder.currentHolder === 1
          );

          const { postalAddresses } = firstHolder?.formValues || {};

          const cityFirstHolder = postalAddresses?.city;

          return addressesSameAsFirstHolders ? cityFirstHolder : value;
        },
      },
      {
        name: "postalAddresses.state",
        type: "text",
        label: labels[language].postalAddresses.state,
        validation: validations.smallStringRequired,
        initialValue: "",
        columns: 2,
        placeholder: placeholders[language].postalAddresses.state,
        allConditionalsTrue: true,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders, postalAddressDifferent } =
            values;
          return currentHolder !== 1
            ? !addressesSameAsFirstHolders && !!postalAddressDifferent
            : !!postalAddressDifferent;
        },
        formatInPreConfirmation: (value, formValues) => {
          const { addressesSameAsFirstHolders } = formValues || {};

          const firstHolder = holdersData.find(
            (holder) => holder.currentHolder === 1
          );

          const { postalAddresses } = firstHolder?.formValues || {};

          const stateFirstHolder = postalAddresses?.state;

          return addressesSameAsFirstHolders ? stateFirstHolder : value;
        },
      },
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "section5",
        text: (
          <>
            <p className={formSectionTitle}>
              {texts[language].prefix} 5: {texts[language].section5}
            </p>
          </>
        ),
        conditional: {
          name: "addressesSameAsFirstHolders",
          value: false,
        },
      },
      {
        name: "proofOfAddress",
        type: "file",
        label: labels[language].proofOfAddress,
        tooltip: {
          label: "",
          content: tooltips[language].proofOfAddress,
          width: "15.625rem",
          icon: InfoIcon,
        },
        validation: validations.fileRequired(
          validProofOfAddressTypes,
          maxFileSize,
          minFileSize
        ),
        initialValue: "",
        infoText: fileInfoText(maxFileSize)[language],
        infoTextPosition: "bottom",
        maxSize: maxFileSize,
        validTypes: validProofOfAddressTypes,
        conditionalBool: (values) => {
          const { addressesSameAsFirstHolders } = values;
          return currentHolder !== 1 ? !addressesSameAsFirstHolders : true;
        },
      },
    ],
    onContinue: async (values: FormikValues, dispatch, state) => {
      try {
        if (!state || !dispatch)
          throw new Error("Error: state or dispatch not found");

        const formValues = values as SecuritiesFormikValues["stepOne"];

        const {
          name,
          lastName,
          motherName,
          gender,
          countryOfBirth,
          birthDate,
          civilStatus,
          educationLevel,
          dependants,
          mobilePhoneAR = "",
          mobilePhoneUS = "",
          homePhoneAR = "",
          homePhoneUS = "",
          workPhoneAR = "",
          workPhoneUS = "",
          addressesSameAsFirstHolders,
          taxAddresses,
          postalAddressDifferent,
          postalAddresses,
          proofOfAddress,
        } = formValues;

        const {
          initialValidationData,
          USCitizen,
          accountHolders,
          taxId,
          holdersData,
          holdersMarried,
          sendedSteps,
        } = state.securities;

        const firstHolder = holdersData.find(
          (holder) => holder.currentHolder === 1
        );

        const landline: StepOneSecuritiesType["personalInformation"]["landline"] =
          (USCitizen && homePhoneUS) || (!USCitizen && homePhoneAR)
            ? `+${USCitizen ? homePhoneUS : homePhoneAR}`
            : "";
        const workPhone: StepOneSecuritiesType["personalInformation"]["workPhone"] =
          (USCitizen && workPhoneUS) || (!USCitizen && workPhoneAR)
            ? `+${USCitizen ? workPhoneUS : workPhoneAR}`
            : "";

        const file = proofOfAddress
          ? serializableFileAsFile(proofOfAddress)
          : addressesSameAsFirstHolders &&
            !!firstHolder?.formValues?.proofOfAddress
          ? serializableFileAsFile(firstHolder?.formValues?.proofOfAddress)
          : null;

        if (!!file && !isEmptyFile(file)) {
          await sendIndividualDocument({
            draftUUID: state.securities.draftUUID,
            cuit: taxId,
            documentType: "PROOF_OF_ADDRESS",
            file,
          });
          saveFileToFirestore?.(file.name, "PROOF_OF_ADDRESS");
        }

        if (currentHolder === 1) {
          const step: StepOneSecuritiesType = {
            "@type": "1_SECURITY",
            referredByCommercialTeam: !!initialValidationData.referred,
            referrer: initialValidationData.referred,
            taxNumber: initialValidationData.taxId,
            accountHolderSize: accountHolders,
            holdersAreMarried: !!holdersMarried,
            personalInformation: {
              isAmericanCitizen: USCitizen,
              completeName: `${name} ${lastName}`,
              motherLastName: motherName,
              gender,
              countryOfBirth,
              birthDate: parseDateToSend(birthDate),
              civilStatus,
              cellPhone: `+${USCitizen ? mobilePhoneUS : mobilePhoneAR}`,
              ...(landline ? { landline } : {}),
              ...(workPhone ? { workPhone } : {}),
              email: initialValidationData.email,
              educationLevel,
              numberOfDependants: parseInt(dependants),
              isSameAddress: !postalAddressDifferent,
              taxDomicile: {
                address: taxAddresses.fullAddress,
                postalCode: taxAddresses.zipCode,
                city: taxAddresses.city,
                province: taxAddresses.state,
                country: taxAddresses.country,
                ...(taxAddresses.floor ? { floor: taxAddresses.floor } : {}),
                ...(taxAddresses.apartment
                  ? { apartment: taxAddresses.apartment }
                  : {}),
              },
              postalDomicile:
                postalAddressDifferent && postalAddresses
                  ? {
                      address: postalAddresses.fullAddress,
                      postalCode: postalAddresses.zipCode,
                      city: postalAddresses.city,
                      province: postalAddresses.state,
                      country: postalAddresses.country,
                      ...(postalAddresses.floor
                        ? { floor: postalAddresses.floor }
                        : {}),
                      ...(postalAddresses.apartment
                        ? { apartment: postalAddresses.apartment }
                        : {}),
                    }
                  : {
                      address: taxAddresses.fullAddress,
                      postalCode: taxAddresses.zipCode,
                      city: taxAddresses.city,
                      province: taxAddresses.state,
                      country: taxAddresses.country,
                      ...(taxAddresses.floor
                        ? { floor: taxAddresses.floor }
                        : {}),
                      ...(taxAddresses.apartment
                        ? { apartment: taxAddresses.apartment }
                        : {}),
                    },
            },
          };

          // Previous sent steps
          const previousSteps = sendedSteps.filter(
            (step: StepEndpointSecurities) => step["@type"] !== "1_SECURITY"
          );

          const body: CreateDraftProps["body"] = {
            companyType: "ALLARIA_SECURITY",
            personType: "PHYSICAL",
            email: initialValidationData.email,
            steps: [...previousSteps, step],
          };

          await sendStep({ stepId: 1, body });
          dispatch(addSendedStep(step));
          dispatch(onUpdateStep(2));
          dispatch(onUpdateCompletedStep(1));
        } else if (addressesSameAsFirstHolders) {
          // Complete holder address with first holder address form values, if it is the same address
          const holderTaxDomicile = firstHolder?.formValues?.taxAddresses;

          const holderPostalDomicile =
            firstHolder?.formValues?.postalAddressDifferent &&
            firstHolder?.formValues?.postalAddresses
              ? firstHolder?.formValues?.postalAddresses
              : holderTaxDomicile;

          dispatch(
            onUpdateFormData({
              postalAddressDifferent:
                firstHolder?.formValues?.postalAddressDifferent,
              taxAddresses: holderTaxDomicile,
              postalAddresses: holderPostalDomicile,
            })
          );
          dispatch(onUpdateStep(2));
          dispatch(onUpdateCompletedStep(1));

          // form values to update on formik
          return {
            newValuesToForm: {
              postalAddressDifferent:
                firstHolder?.formValues?.postalAddressDifferent,
              taxAddresses: holderTaxDomicile,
              postalAddresses: holderPostalDomicile,
            },
          };
        }
      } catch (error) {
        console.error("Error step one: ", error);
        throw new Error("Error al enviar los datos en el paso 1");
      }
    },
  };
};

export default StepOne;
